import React from 'react';
import { View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';

import * as Layout from '@components/screen/Layout';
import SociollaCard from '@components/sociolla/SociollaCard';
import SociollaText from '@components/sociolla/SociollaText';
import Highcharts from '@components/chart/Highcharts';
import Colors from '@global/colors';

const SLICES = [
  { key: 'visited', label: 'Visited', color: Colors.blue0 },
  { key: 'not_visited', label: 'Not Visited', color: Colors.yellow0 },
  { key: 'engage', label: 'Engage Potential Customer', color: Colors.grey13 },
];

const SalesmanVisitStatusChart = ({ status, loading, error, style }) => {
  const counts = SLICES.map((slice) => status?.[slice.key] || 0);
  const total = counts.reduce((sum, count) => sum + count, 0);
  const pct = (count) => (total ? ((count / total) * 100).toFixed(1) : '0.0');

  const body = () => {
    if (loading) {
      return <Layout.Loader />;
    }
    if (error) {
      return (
        <SociollaText style={styles.unavailable}>Unavailable</SociollaText>
      );
    }
    return (
      <View style={styles.donutRow}>
        <View style={styles.donut}>
          <Highcharts
            type="pie"
            height={220}
            innerSize="55%"
            showDataLabels={false}
            centerText={String(total)}
            colors={SLICES.map((slice) => slice.color)}
            pieTooltipFormat="{point.name}: <b>{point.y}</b>"
            data={[
              {
                name: '',
                data: SLICES.map((slice, index) => ({
                  name: slice.label,
                  y: counts[index],
                })),
              },
            ]}
          />
        </View>

        <View style={styles.legend}>
          {SLICES.map((slice, index) => (
            <View key={slice.key} style={styles.legendItem}>
              <View style={styles.legendHeader}>
                <View
                  style={[styles.swatch, { backgroundColor: slice.color }]}
                />
                <SociollaText style={styles.legendLabel}>
                  {`${slice.label}:`}
                </SociollaText>
              </View>
              <View style={styles.legendValue}>
                <SociollaText type="medium" style={styles.legendCount}>
                  {counts[index]}
                </SociollaText>
                <SociollaText style={styles.legendPct}>
                  {`(${pct(counts[index])}%)`}
                </SociollaText>
              </View>
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <SociollaCard style={[styles.card, style]}>
      <SociollaText type="medium" style={styles.title}>
        Visit Status
      </SociollaText>
      <View style={styles.body}>{body()}</View>
    </SociollaCard>
  );
};

SalesmanVisitStatusChart.propTypes = {
  status: PropTypes.shape({
    visited: PropTypes.number,
    not_visited: PropTypes.number,
    engage: PropTypes.number,
  }),
  loading: PropTypes.bool,
  error: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

SalesmanVisitStatusChart.defaultProps = {
  status: null,
  loading: false,
  error: false,
  style: null,
};

const styles = StyleSheet.create({
  body: { justifyContent: 'center', minHeight: 220 },
  card: { marginBottom: 0, padding: 16 },
  // Explicit height too: on iOS the wrapping row measures the WebView without its basis.
  donut: { flexBasis: 220, flexGrow: 0, height: 220, width: 220 },
  donutRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'center',
  },
  legend: { flexBasis: 140, flexGrow: 1 },
  legendCount: { fontSize: 14, marginRight: 6 },
  legendHeader: { alignItems: 'center', flexDirection: 'row' },
  legendItem: { marginBottom: 12 },
  legendLabel: { color: Colors.grey13, fontSize: 13 },
  legendPct: { color: Colors.grey13, fontSize: 13 },
  legendValue: { alignItems: 'center', flexDirection: 'row', paddingLeft: 18 },
  swatch: { borderRadius: 2, height: 10, marginRight: 8, width: 10 },
  title: { fontSize: 16, marginBottom: 16 },
  unavailable: { color: Colors.red0, fontSize: 16, textAlign: 'center' },
});

export default SalesmanVisitStatusChart;
